import { Fragment, useMemo, useState } from "react";
import { tzAbbrev } from "../lib/rfHealth";
import { useGlobalQuery } from "../api/useGlobalQuery";
import { FIRST_CALCULATION_TEXT } from '../lib/definitions/analysis';
import {
  ALL,
  axisLabelIndexes,
  DASH,
  hopView,
  hourBars,
  isPending,
  KM_TIP,
  kmText,
  LOCATION_TIP,
  localDayTime,
  loopExamples,
  loopRows,
  LOOP_PACKETS_TIP,
  LOOPS_TIP,
  NO_LOOPS_TEXT,
  NO_ROUTES_TEXT,
  OBSERVER_TIP,
  ONE_BYTE_TIP,
  packetsText,
  PACKETS_TIP,
  pathPieces,
  PATH_BADGE,
  pathTag,
  PATHS_GROUPS,
  PATHS_WINDOWS,
  pctText,
  PCT_TIP,
  routeRows,
  SHARED_ID_NOTE,
  trendBars,
  trendSummary,
  TREND_TIP,
  UNCLEAR_TIP,
  worstText,
  WORST_TIP,
  type PathsPayload,
  type PathsResponse,
  type PathsRoute,
  type PathsWindow,
} from "../lib/paths";
import { HoverTip } from "./HoverTip";
import { HopsSection } from "./TopologySections";
import { useBackbonePayload } from "./useBackbonePayload";
import { BACKBONE_LOADING, BACKBONE_PENDING, BACKBONE_UNAVAILABLE } from '../lib/definitions/topology';

// Analysis > MeshCore > Paths. Everything the page says is decided by
// lib/paths.ts; this file only lays it out. Two views: the most common routes
// (sub-tabs by length) and the repeaters whose IDs loop.

type View = "routes" | "loops" | "hops";

/** One hop: a repeater's name linked to its page, or an unclear hop shown by its short ID with a tip. */
function Hop({ p, token, strong = false }: { p: PathsPayload; token: number; strong?: boolean }) {
  const v = hopView(p, token);
  if (v.nodeId !== null) {
    return (
      <a
        href={`#/nodes/${encodeURIComponent(v.nodeId)}`}
        className={`node-name-link paths-hop${strong ? " paths-hop-strong" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {v.label}
      </a>
    );
  }
  return (
    <HoverTip text={v.tip ?? ""} className="paths-unclear-tip">
      <span className={`paths-hop paths-hop-unclear${strong ? " paths-hop-strong" : ""}`}>{v.label}</span>
    </HoverTip>
  );
}

/** The "Resolved By Path" tag next to a hop that only the rest of the path could name. */
function PathTag({ byPath }: { byPath: boolean }) {
  const text = pathTag(byPath);
  if (!text) return null;
  return (
    <HoverTip text={text} className="paths-unclear-tip">
      <span className="path-tag" aria-label={text}>{PATH_BADGE}</span>
    </HoverTip>
  );
}

function Arrow() {
  return (
    <span className="paths-arrow" aria-hidden="true">
      {" → "}
    </span>
  );
}

function HopList({ p, tokens, from = 0, to }: { p: PathsPayload; tokens: number[]; from?: number; to?: number }) {
  const end = to ?? tokens.length;
  return (
    <>
      {tokens.slice(from, end).map((t, i) => (
        <Fragment key={`${from + i}-${t}`}>
          {i > 0 && <Arrow />}
          <Hop p={p} token={t} />
        </Fragment>
      ))}
    </>
  );
}

// --- trend: seven small daily bars ---------------------------------------------------

const SPARK = { w: 7, gap: 2, h: 24 };

function Trend({ trendStart, tr }: { trendStart: string; tr: number[] }) {
  const bars = useMemo(() => trendBars(trendStart, tr), [trendStart, tr]);
  const width = bars.length * (SPARK.w + SPARK.gap) - SPARK.gap;
  return (
    <HoverTip text={trendSummary(trendStart, tr)} className="paths-trend-tip" placement="left">
      <svg width={width} height={SPARK.h} role="img" aria-label="Packets per day, last 7 days" className="paths-trend">
        {bars.map((b, i) => {
          const h = b.n > 0 ? Math.max(2, Math.round(b.height * SPARK.h)) : 1;
          return <rect key={i} x={i * (SPARK.w + SPARK.gap)} y={SPARK.h - h} width={SPARK.w} height={h} className={b.n > 0 ? "paths-bar" : "paths-bar-empty"} />;
        })}
      </svg>
    </HoverTip>
  );
}

// --- activity by hour -------------------------------------------------------------------

const HOUR_CHART = { h: 110, bottom: 16, top: 6 };

function HourChart({ p, route }: { p: PathsPayload; route: PathsRoute }) {
  const bars = useMemo(() => hourBars(p.window_start, route.h, p.window), [p.window_start, route.h, p.window]);
  const labelled = useMemo(() => new Set(axisLabelIndexes(bars.length, p.window)), [bars.length, p.window]);
  const slot = 100 / Math.max(1, bars.length);
  const plotH = HOUR_CHART.h - HOUR_CHART.bottom - HOUR_CHART.top;
  return (
    <div className="paths-hours" style={{ height: HOUR_CHART.h }}>
      <svg viewBox={`0 0 100 ${HOUR_CHART.h}`} preserveAspectRatio="none" width="100%" height={HOUR_CHART.h} role="img" aria-label="Packets per hour on this route">
        {bars.map((b, i) => {
          const h = b.n > 0 ? Math.max(1, b.height * plotH) : 0;
          return <rect key={i} x={i * slot + slot * 0.12} y={HOUR_CHART.top + plotH - h} width={slot * 0.76} height={h} className="paths-bar" />;
        })}
      </svg>
      {bars.map((b, i) => (
        <div key={i} className="paths-hour-hit" style={{ left: `${i * slot}%`, width: `${slot}%`, height: HOUR_CHART.h - HOUR_CHART.bottom }}>
          <HoverTip block text={b.tip}>
            <span className="paths-hit-fill" />
          </HoverTip>
        </div>
      ))}
      {bars.map((b, i) =>
        labelled.has(i) ? (
          <span key={`l${i}`} className="paths-hour-label" style={{ left: `${i * slot + slot / 2}%` }}>
            {b.label}
          </span>
        ) : null,
      )}
    </div>
  );
}

// --- route detail ----------------------------------------------------------------------------

// The viewer's browser time-zone abbreviation, " (MDT)", for the time labels.
const tzSuffix = (() => {
  const tz = tzAbbrev(null);
  return tz ? ` (${tz})` : "";
})();

function RouteDetail({ p, route }: { p: PathsPayload; route: PathsRoute }) {
  const len = route.t.length;
  return (
    <div className="paths-detail">
      <h4 className="rf-section-h">PACKETS BY HOUR{tzSuffix}</h4>
      <HourChart p={p} route={route} />

      <div className="paths-detail-cols">
        <div>
          <h4 className="rf-section-h">
            <HoverTip text={OBSERVER_TIP}>
              <span>OBSERVERS</span>
            </HoverTip>
          </h4>
          {route.o.length === 0 ? (
            <div className="rf-sub">{DASH}</div>
          ) : (
            <div className="node-list-table-wrap rf-table-wrap">
              <table className="node-list-table">
                <thead>
                  <tr>
                    <th>Observer</th>
                    <th className="rf-num">Packets</th>
                  </tr>
                </thead>
                <tbody>
                  {route.o.map((o, i) => (
                    <tr key={`${o.name}-${i}`}>
                      <td>{o.name}</td>
                      <td className="rf-num">{packetsText(o.n)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <h4 className="rf-section-h">
            <HoverTip text={KM_TIP}>
              <span>HOP DISTANCES</span>
            </HoverTip>
          </h4>
          <div className="node-list-table-wrap rf-table-wrap">
            <table className="node-list-table">
              <thead>
                <tr>
                  <th>Hop</th>
                  <th className="rf-num">Distance</th>
                </tr>
              </thead>
              <tbody>
                {route.km.map((km, i) => (
                  <tr key={i}>
                    <td>
                      <Hop p={p} token={route.t[i]} />
                      <Arrow />
                      <Hop p={p} token={route.t[i + 1]} />
                    </td>
                    <td className="rf-num">{kmText(km)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <h4 className="rf-section-h">FULL PATHS</h4>
      <div className="node-list-table-wrap rf-table-wrap">
        <table className="node-list-table">
          <thead>
            <tr>
              <th>Path</th>
              <th className="rf-num">Packets</th>
            </tr>
          </thead>
          <tbody>
            {route.p.map((fp, i) => (
              <tr key={i}>
                <td className="paths-fullpath">
                  {fp.o !== null && (
                    <>
                      <span className="paths-origin">Origin </span>
                      <Hop p={p} token={fp.o} />
                      <Arrow />
                    </>
                  )}
                  {pathPieces(fp, len).map((piece, j) => (
                    <Fragment key={j}>
                      {j > 0 && <Arrow />}
                      <Hop p={p} token={piece.token} strong={piece.inRoute} />
                      <PathTag byPath={piece.byPath} />
                    </Fragment>
                  ))}
                </td>
                <td className="rf-num">{packetsText(fp.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- common routes ----------------------------------------------------------------------------------

function RoutesView({ p, loc, includeUnclear }: { p: PathsPayload; loc: string; includeUnclear: boolean }) {
  const [group, setGroup] = useState("2");
  const [open, setOpen] = useState<number | null>(null);
  const rows = useMemo(() => routeRows(p, loc, includeUnclear, group), [p, loc, includeUnclear, group]);

  function toggle(id: number) {
    setOpen((cur) => (cur === id ? null : id));
  }

  return (
    <div>
      <div className="theme-toggle audit-tab-row paths-groups" role="group" aria-label="Route length">
        {PATHS_GROUPS.map((g) => (
          <button key={g.key} type="button" data-active={group === g.key} onClick={() => { setGroup(g.key); setOpen(null); }}>
            {g.label}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="empty-state">{NO_ROUTES_TEXT}</div>
      ) : (
        <div className="node-list-table-wrap">
          <table className="node-list-table paths-table">
            <thead>
              <tr>
                <th>Route</th>
                <th className="rf-num">
                  <HoverTip text={PACKETS_TIP}>
                    <span>Packets</span>
                  </HoverTip>
                </th>
                <th className="rf-num">
                  <HoverTip text={PCT_TIP}>
                    <span>%</span>
                  </HoverTip>
                </th>
                <th className="paths-trend-col">
                  <HoverTip text={TREND_TIP}>
                    <span>Trend</span>
                  </HoverTip>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ id, route }) => (
                <Fragment key={id}>
                  <tr
                    className="paths-row"
                    data-open={open === id}
                    tabIndex={0}
                    aria-expanded={open === id}
                    onClick={() => toggle(id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle(id);
                      }
                    }}
                  >
                    <td className="paths-route">
                      <HopList p={p} tokens={route.t} />
                    </td>
                    <td className="rf-num">{packetsText(route.n)}</td>
                    <td className="rf-num">{pctText(route.n, p.packets_total)}</td>
                    <td className="paths-trend-col">
                      <Trend trendStart={p.trend_start} tr={route.tr} />
                    </td>
                  </tr>
                  {open === id && (
                    <tr className="paths-detail-row">
                      <td colSpan={4}>
                        <RouteDetail p={p} route={route} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- loops ---------------------------------------------------------------------------------------------

function LoopsView({ p, loc, showOneByte }: { p: PathsPayload; loc: string; showOneByte: boolean }) {
  const [open, setOpen] = useState<number | null>(null);
  const rows = useMemo(() => loopRows(p, loc, showOneByte), [p, loc, showOneByte]);

  if (rows.length === 0) return <div className="empty-state">{NO_LOOPS_TEXT}</div>;

  return (
    <div className="node-list-table-wrap">
      <table className="node-list-table paths-table">
        <thead>
          <tr>
            <th>Repeater</th>
            <th className="rf-num">
              <HoverTip text={LOOPS_TIP}>
                <span>Loops</span>
              </HoverTip>
            </th>
            <th className="rf-num">
              <HoverTip text={WORST_TIP}>
                <span>Worst</span>
              </HoverTip>
            </th>
            <th className="rf-num">
              <HoverTip text={LOOP_PACKETS_TIP}>
                <span>Packets affected</span>
              </HoverTip>
            </th>
            <th>Last seen{tzSuffix}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ row, agg, sharedIdMark }) => {
            const examples = open === row.node ? loopExamples(row, showOneByte) : [];
            return (
              <Fragment key={row.node}>
                <tr
                  className="paths-row"
                  data-open={open === row.node}
                  tabIndex={0}
                  aria-expanded={open === row.node}
                  onClick={() => setOpen((cur) => (cur === row.node ? null : row.node))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpen((cur) => (cur === row.node ? null : row.node));
                    }
                  }}
                >
                  <td>
                    <Hop p={p} token={row.node} />
                    {sharedIdMark && (
                      <HoverTip text={ONE_BYTE_TIP} className="paths-shared-tip">
                        <span className="paths-shared">{SHARED_ID_NOTE}</span>
                      </HoverTip>
                    )}
                  </td>
                  <td className="rf-num">{packetsText(agg.loops)}</td>
                  <td className="rf-num">{worstText(agg.worst)}</td>
                  <td className="rf-num">{packetsText(agg.packets)}</td>
                  <td>{localDayTime(agg.last_seen)}</td>
                </tr>
                {open === row.node && (
                  <tr className="paths-detail-row">
                    <td colSpan={5}>
                      <div className="paths-detail">
                        <div className="node-list-table-wrap rf-table-wrap">
                          <table className="node-list-table">
                            <thead>
                              <tr>
                                <th>Time{tzSuffix}</th>
                                <th>Observer</th>
                                <th>Path</th>
                              </tr>
                            </thead>
                            <tbody>
                              {examples.map((ex, i) => (
                                <tr key={i}>
                                  <td className="rf-num">{localDayTime(ex.time)}</td>
                                  <td>{ex.observer ?? DASH}</td>
                                  <td className="paths-fullpath">
                                    {ex.path.map((t, j) => (
                                      <Fragment key={j}>
                                        {j > 0 && <Arrow />}
                                        <span className={ex.marks.includes(j) ? "paths-loop-hop" : undefined}>
                                          <Hop p={p} token={t} strong={ex.marks.includes(j)} />
                                        </span>
                                      </Fragment>
                                    ))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Hops view (2026-10-07): the hops figures from the backbone snapshot payload,
// for the window the Paths tab shows.
function HopsView({ windowKey }: { windowKey: PathsWindow }) {
  const data = useBackbonePayload(windowKey);
  if (data === null) return <div className="node-detail-note">{BACKBONE_LOADING}</div>;
  if (data === "pending") return <div className="node-detail-note">{BACKBONE_PENDING}</div>;
  if (data === "unavailable") return <div className="node-detail-note">{BACKBONE_UNAVAILABLE}</div>;
  return <HopsSection hops={data.hops} window={windowKey} />;
}

// --- page ---------------------------------------------------------------------------------------------------

export function PathsTab() {
  const [windowKey, setWindowKey] = useState<PathsWindow>("24h");
  const [loc, setLoc] = useState<string>(ALL);
  const [view, setView] = useState<View>("routes");
  const [includeUnclear, setIncludeUnclear] = useState(false);
  const [showOneByte, setShowOneByte] = useState(false);

  // Stored snapshot, kept in the shared query cache: switching tabs or window
  // never blanks data that is already there.
  const q = useGlobalQuery<PathsResponse>(
    ["paths", "meshcore", windowKey],
    `/api/paths/meshcore?window=${windowKey}`,
    60000,
  );
  const data = q.data;
  const payload: PathsPayload | null = data && !isPending(data) ? data : null;

  return (
    <div className="paths-tab">
      <div className="node-list-toolbar">
        <div className="theme-toggle" role="group" aria-label="Window">
          {PATHS_WINDOWS.map((w) => (
            <button key={w} type="button" data-active={windowKey === w} onClick={() => setWindowKey(w)}>
              {w}
            </button>
          ))}
        </div>
        <div className="theme-toggle" role="group" aria-label="View">
          <button type="button" data-active={view === "routes"} onClick={() => setView("routes")}>
            Common Routes
          </button>
          <button type="button" data-active={view === "loops"} onClick={() => setView("loops")}>
            Loops
          </button>
          <button type="button" data-active={view === "hops"} onClick={() => setView("hops")}>
            Hops
          </button>
        </div>
        {view !== "hops" && (
        <label className="rf-filter">
          <HoverTip text={LOCATION_TIP}>
            <span>Location</span>
          </HoverTip>{" "}
          <select value={loc} onChange={(e) => setLoc(e.target.value)}>
            <option value={ALL}>All</option>
            {(payload?.areas ?? []).map((a) => (
              <option key={a.code} value={a.code}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        )}
        {view === "routes" ? (
          <label className="rf-filter">
            <input type="checkbox" checked={includeUnclear} onChange={(e) => setIncludeUnclear(e.target.checked)} />
            <HoverTip text={UNCLEAR_TIP}>
              <span>Include unclear hops</span>
            </HoverTip>
          </label>
        ) : view === "loops" ? (
          <label className="rf-filter">
            <input type="checkbox" checked={showOneByte} onChange={(e) => setShowOneByte(e.target.checked)} />
            <HoverTip text={ONE_BYTE_TIP}>
              <span>Show 1-byte ID loops</span>
            </HoverTip>
          </label>
        ) : null}
      </div>

      {view === "hops" && <HopsView windowKey={windowKey} />}
      {view !== "hops" && data === null && q.loading && <div className="empty-state">Loading…</div>}
      {view !== "hops" && data === null && !q.loading && q.error && <div className="node-detail-note">Not available right now.</div>}
      {view !== "hops" && data && isPending(data) && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}
      {payload && view === "routes" && <RoutesView p={payload} loc={loc} includeUnclear={includeUnclear} />}
      {payload && view === "loops" && <LoopsView p={payload} loc={loc} showOneByte={showOneByte} />}
    </div>
  );
}
